import { Navigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Card } from '@/components/ui'
import { AgentProviderTokenSection } from '@/components/AgentProviderTokenSection'
import { TitanEndpointSection } from '@/components/TitanEndpointSection'
import { useAuth } from '@/hooks/useAuth'
import { useTenant } from '@/contexts/TenantContext'
import { Permissions } from '@/lib/permissions'
import {
  useTenantClaudeApiKey,
  useSaveTenantClaudeApiKey,
  useDeleteTenantClaudeApiKey,
  useTenantCopilotApiKey,
  useSaveTenantCopilotApiKey,
  useDeleteTenantCopilotApiKey,
  useTenantTitanSettings,
  useSaveTenantTitanSettings,
  useDeleteTenantTitanSettings,
} from '@/hooks'

export function TenantSettingsPage() {
  const { hasPermission, isAdmin, isLoading: authLoading } = useAuth()
  const { currentTenant } = useTenant()

  const canManage = isAdmin || hasPermission(Permissions.Host.Tenant.Settings.Manage)

  const { data, isLoading } = useTenantClaudeApiKey(canManage)
  const saveKey = useSaveTenantClaudeApiKey()
  const deleteKey = useDeleteTenantClaudeApiKey()

  const { data: copilotData, isLoading: isCopilotLoading } = useTenantCopilotApiKey(canManage)
  const saveCopilotKey = useSaveTenantCopilotApiKey()
  const deleteCopilotKey = useDeleteTenantCopilotApiKey()

  const { data: titanData, isLoading: isTitanLoading, error: titanError, refetch: refetchTitan } = useTenantTitanSettings(canManage)
  const saveTitan = useSaveTenantTitanSettings()
  const deleteTitan = useDeleteTenantTitanSettings()

  if (authLoading) return null

  if (!canManage) {
    return <Navigate to="/projects" replace />
  }

  const tenantName = currentTenant?.name ?? ''

  return (
    <div className="space-y-5" aria-label="Tenant settings">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Settings{tenantName ? ` — ${tenantName}` : ''}</h1>
      </div>

      <Card className="p-6">
        <AgentProviderTokenSection
          claude={{
            title: 'Claude Code API Key',
            description: 'Anthropic API key used by all workflows and chats in this tenant unless a user supplies their own OAuth token or a project overrides it.',
            hasKey: data?.hasKey ?? false,
            isLoading: isLoading,
            isSaving: saveKey.isPending,
            isDeleting: deleteKey.isPending,
            onSave: async (apiKey) => {
              await saveKey.mutateAsync(apiKey)
              toast.success('Tenant Claude API key saved.')
            },
            onDelete: async () => {
              await deleteKey.mutateAsync()
              toast.success('Tenant Claude API key removed.')
            },
            testIdPrefix: 'tenant-claude-api-key',
          }}
          copilot={{
            title: 'Copilot CLI GitHub Token',
            description: 'GitHub token used by all Copilot CLI workflows in this tenant unless a user supplies their own GitHub token or a project overrides it.',
            hasKey: copilotData?.hasKey ?? false,
            isLoading: isCopilotLoading,
            isSaving: saveCopilotKey.isPending,
            isDeleting: deleteCopilotKey.isPending,
            onSave: async (apiKey) => {
              await saveCopilotKey.mutateAsync(apiKey)
              toast.success('Tenant Copilot CLI token saved.')
            },
            onDelete: async () => {
              await deleteCopilotKey.mutateAsync()
              toast.success('Tenant Copilot CLI token removed.')
            },
            testIdPrefix: 'tenant-copilot-api-key',
          }}
          titanHasKey={!!titanData?.endpoint}
          titanIsLoading={isTitanLoading}
          titan={
            <TitanEndpointSection
              key={`${titanData?.endpoint ?? ""}|${titanData?.caCertificate ?? ""}`}
              title="Qubiz Titan"
              description="Address of the Qubiz Titan instance used by workflows in this tenant. Each user supplies their own personal key."
              endpoint={titanData?.endpoint ?? null}
              caCertificate={titanData?.caCertificate ?? null}
              isLoading={isTitanLoading}
              loadError={titanError}
              onRetryLoad={() => { void refetchTitan() }}
              isSaving={saveTitan.isPending}
              isDeleting={deleteTitan.isPending}
              onSave={async (endpoint, caCertificate) => {
                await saveTitan.mutateAsync({ endpoint, caCertificate })
                toast.success('Qubiz Titan settings saved.')
              }}
              onDelete={async () => {
                await deleteTitan.mutateAsync()
                toast.success('Qubiz Titan settings removed.')
              }}
              testIdPrefix="tenant-titan"
            />
          }
          testIdPrefix="tenant-ai-provider"
        />
      </Card>
    </div>
  )
}
